import {
  cancelBookingPaymentAttempts,
  fetchBooking,
  getBookingPaymentStatus,
} from '@/features/purchase/purchaseService';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import { TransAlert } from '@/i18n/trans/alert';
import type { BookingDTO } from '@/types/dto/agent';
import type {
  KlixPaymentPendingStorageData,
  PurchaseFlowBookingStorageData,
} from '@/types/storage';
import { getBookingFulfillments, getBookingTotalPrice } from '@/utils/booking';
import {
  buildPaymentType,
  getBookingPromoCodes,
  getBookingVoucherCodes,
} from '@/utils/gtmPayloads';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 7;

interface UsePaymentStatusCheckParams {
  booking: BookingDTO | null;
  enabled?: boolean;
  isChangeFlow?: boolean;
  onPaymentCompleted?: () => void;
}

export const usePaymentStatusCheck = ({
  booking,
  enabled = true,
  isChangeFlow = false,
  onPaymentCompleted,
}: UsePaymentStatusCheckParams) => {
  const navigate = useNavigate();
  const { setValue: setCheckoutSuccessSessionStorage } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );
  const {
    value: klixPending,
    setValue: setKlixPending,
    removeValue: clearKlixPending,
  } = useStorage<KlixPaymentPendingStorageData | null>(
    sessionStorage,
    STORAGE_KEYS.KLIX_PAYMENT_PENDING
  );
  const setCheckoutSuccessSessionStorageRef = useRef(
    setCheckoutSuccessSessionStorage
  );
  const clearKlixPendingRef = useRef(clearKlixPending);
  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  const bookingRef = useRef(booking);
  const hasNavigatedToSuccessRef = useRef(false);
  const isNavigatingToSuccessRef = useRef(false);
  const trackedBookingIdRef = useRef(booking?.id);

  setCheckoutSuccessSessionStorageRef.current =
    setCheckoutSuccessSessionStorage;
  clearKlixPendingRef.current = clearKlixPending;
  onPaymentCompletedRef.current = onPaymentCompleted;
  bookingRef.current = booking;

  if (booking?.id !== trackedBookingIdRef.current) {
    trackedBookingIdRef.current = booking?.id;
    hasNavigatedToSuccessRef.current = false;
    isNavigatingToSuccessRef.current = false;
  }

  const isConfirmingKlixPayment =
    !!booking?.id &&
    !!klixPending?.bookingId &&
    klixPending.bookingId === booking.id;

  const hasPendingExchange = (booking?.exchangeOperations ?? []).some(
    (operation) =>
      operation.status !== 'FULFILLED' && operation.status !== 'ERROR'
  );

  useEffect(() => {
    if (!enabled || !booking?.id || !booking.code) return;

    const bookingId = booking.id;
    const bookingCode = booking.code;
    const klixReturn =
      !!klixPending?.bookingId && klixPending.bookingId === bookingId;

    let isCancelled = false;

    const delay = () =>
      new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const pollPaymentStatus = async (
      id: string,
      maxAttempts: number
    ): Promise<string> => {
      let attempts = 0;
      let lastStatus = 'pending';

      while (attempts < maxAttempts && !isCancelled) {
        await delay();
        if (isCancelled) return lastStatus;

        attempts++;

        try {
          lastStatus =
            (await getBookingPaymentStatus(id, hasPendingExchange)) ??
            'pending';
          if (lastStatus !== 'pending') {
            return lastStatus;
          }
        } catch {
          break;
        }
      }

      return lastStatus;
    };

    const navigateToSuccess = async () => {
      if (
        isCancelled ||
        hasNavigatedToSuccessRef.current ||
        isNavigatingToSuccessRef.current
      ) {
        return;
      }

      isNavigatingToSuccessRef.current = true;
      onPaymentCompletedRef.current?.();

      if (isChangeFlow) {
        hasNavigatedToSuccessRef.current = true;
        clearKlixPendingRef.current();
        navigate('/manage-booking/details', {
          replace: true,
          state: { completedChangeFlow: true },
        });
        return;
      }

      const currentBooking = bookingRef.current;
      if (!currentBooking) {
        isNavigatingToSuccessRef.current = false;
        return;
      }

      let bookingToUse = currentBooking;
      try {
        const refreshedBooking = await fetchBooking(bookingId);
        bookingToUse = refreshedBooking ?? currentBooking;
      } catch (error) {
        console.error(
          'Failed to refresh booking for success navigation:',
          error
        );
      }

      if (isCancelled) {
        isNavigatingToSuccessRef.current = false;
        return;
      }

      hasNavigatedToSuccessRef.current = true;
      clearKlixPendingRef.current();

      setCheckoutSuccessSessionStorageRef.current({
        bookingId,
        code: bookingCode,
        payerEmail:
          bookingToUse.purchaser?.contactInformation?.emailAddress ?? '',
        fulfillments: getBookingFulfillments(bookingToUse),
        totalPrice:
          getBookingTotalPrice(currentBooking) ??
          getBookingTotalPrice(bookingToUse),
        // This path only completes Klix redirect returns and Adyen
        // status-recovery polling, so the rail is derivable (BR-57983).
        // Discount context is read from the pre-refresh booking — after
        // payment the refreshed booking no longer carries pendingVouchers.
        paymentType: buildPaymentType(
          klixReturn ? 'Klix' : 'Adyen',
          currentBooking
        ),
        promoCodes: getBookingPromoCodes(currentBooking),
        vouchers: getBookingVoucherCodes(currentBooking),
      });

      navigate('/success', { replace: true });
    };

    const handleKlixReturn = async () => {
      try {
        while (!isCancelled && !hasNavigatedToSuccessRef.current) {
          const status =
            (await getBookingPaymentStatus(bookingId, hasPendingExchange)) ??
            'pending';

          if (isCancelled || hasNavigatedToSuccessRef.current) return;

          if (status === 'paid') {
            await navigateToSuccess();
            return;
          }

          if (status === 'not_paid') {
            clearKlixPendingRef.current();
            toast.error(<TransAlert i18nKey="paymentFailed" />);
            return;
          }

          await delay();
        }
      } catch (error) {
        console.error('Error checking Klix payment status:', error);
        if (!isCancelled) {
          clearKlixPendingRef.current();
          toast.error(<TransAlert i18nKey="paymentFailed" />);
        }
      }
    };

    const handlePaymentStatus = async () => {
      if (hasNavigatedToSuccessRef.current) return;

      if (klixReturn) {
        await handleKlixReturn();
        return;
      }

      if (hasPendingExchange) return;

      try {
        const initialStatus =
          (await getBookingPaymentStatus(bookingId)) ?? 'pending';

        if (isCancelled || hasNavigatedToSuccessRef.current) return;

        if (initialStatus === 'paid') {
          await navigateToSuccess();
          return;
        }

        if (initialStatus === 'pending') {
          const finalStatus = await pollPaymentStatus(
            bookingId,
            MAX_POLL_ATTEMPTS
          );

          if (isCancelled || hasNavigatedToSuccessRef.current) return;

          if (finalStatus === 'paid') {
            await navigateToSuccess();
            return;
          }

          if (finalStatus === 'pending') {
            await cancelBookingPaymentAttempts(bookingId);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    void handlePaymentStatus();

    return () => {
      isCancelled = true;
      if (!hasNavigatedToSuccessRef.current) {
        isNavigatingToSuccessRef.current = false;
      }
    };
  }, [
    booking?.id,
    booking?.code,
    enabled,
    hasPendingExchange,
    isChangeFlow,
    isConfirmingKlixPayment,
    klixPending?.bookingId,
    navigate,
  ]);

  return {
    isConfirmingKlixPayment,
    setKlixPaymentPending: setKlixPending,
  };
};
